import { listGameRuleGeneratedDocuments } from "./gameRuleGeneratedDocumentsService";
import { listOpenGenerationSessions } from "./gameRuleKbGenerateService";
import type { GameRuleGeneratedListRow } from "../types/gameRuleGenerated";
import type { KbGenerationSessionListItem } from "../types/gameRuleKbGenerate";
import type {
  GameRuleWorkspaceRow,
  WorkspaceStatusFilter,
  WorkspaceStatusKey,
} from "../types/gameRuleWorkspace";

const sessionStatusLabel = (session: KbGenerationSessionListItem): string => {
  const normalized = session.status.trim().toLowerCase();
  if (normalized === "ai_running") {
    return "In progress · AI running";
  }
  if (normalized === "ai_failed") {
    return "In progress · AI failed";
  }
  return `In progress · Step ${session.currentStep} of 5`;
};

const mapSessionRow = (session: KbGenerationSessionListItem): GameRuleWorkspaceRow => ({
  kind: "session",
  id: session.id,
  name: session.ruleName?.trim() || "Untitled game rule",
  subtitle: `Step ${session.currentStep} of 5`,
  statusLabel: sessionStatusLabel(session),
  statusKey: "in_progress",
  updatedAt: session.updatedAt,
  sectionCount: session.selectedSectionCount,
  reviewerEmail: null,
  currentStep: session.currentStep,
});

const documentStatusKey = (status: string): WorkspaceStatusKey => {
  const normalized = status.trim().toLowerCase();
  if (normalized === "review") return "review";
  if (normalized === "approved") return "approved";
  if (normalized === "draft") return "draft";
  return "review";
};

const documentStatusLabel = (status: string): string => {
  const normalized = status.trim().toLowerCase();
  if (normalized === "review") return "Pending review";
  if (normalized === "approved") return "Approved";
  if (normalized === "draft") return "Draft";
  return status;
};

const mapDocumentRow = (document: GameRuleGeneratedListRow): GameRuleWorkspaceRow => ({
  kind: "document",
  id: document.id,
  name: document.name,
  subtitle: document.fileName,
  statusLabel: documentStatusLabel(document.status),
  statusKey: documentStatusKey(document.status),
  updatedAt: document.generatedAt,
  sectionCount: document.sectionCount,
  reviewerEmail: document.reviewerEmail,
});

const matchesSearch = (row: GameRuleWorkspaceRow, query: string): boolean => {
  const q = query.toLowerCase();
  return (
    row.name.toLowerCase().includes(q) ||
    row.subtitle.toLowerCase().includes(q) ||
    row.statusLabel.toLowerCase().includes(q) ||
    (row.reviewerEmail?.toLowerCase().includes(q) ?? false)
  );
};

const documentStatusParam = (filter: WorkspaceStatusFilter): string => {
  if (filter === "myqueue") return "myqueue";
  if (filter === "review") return "review";
  if (filter === "approved") return "approved";
  if (filter === "draft") return "draft";
  return "all";
};

export type LoadWorkspaceRowsOptions = {
  readonly search?: string;
  readonly statusFilter: WorkspaceStatusFilter;
  readonly currentUserEmail?: string | null;
  readonly documentLimit?: number;
  readonly sessionLimit?: number;
};

export const loadWorkspaceRows = async (
  options: LoadWorkspaceRowsOptions,
): Promise<readonly GameRuleWorkspaceRow[]> => {
  const search = options.search?.trim() ?? "";
  const sessionLimit = options.sessionLimit ?? 100;
  const documentLimit = options.documentLimit ?? 100;
  const filter = options.statusFilter;

  const needSessions = filter === "all" || filter === "in_progress";
  const needDocuments = filter !== "in_progress";

  const [sessions, documentsResult] = await Promise.all([
    needSessions ? listOpenGenerationSessions(sessionLimit) : Promise.resolve([]),
    needDocuments
      ? listGameRuleGeneratedDocuments(
          1,
          documentLimit,
          search || undefined,
          documentStatusParam(filter),
        )
      : Promise.resolve({ items: [] as GameRuleGeneratedListRow[], total: 0 }),
  ]);

  const rows: GameRuleWorkspaceRow[] = [
    ...sessions.map(mapSessionRow),
    ...documentsResult.items.map(mapDocumentRow),
  ];

  let filtered = rows;

  if (filter === "myqueue" && options.currentUserEmail?.trim()) {
    const email = options.currentUserEmail.trim().toLowerCase();
    filtered = filtered.filter(
      (row) =>
        row.kind === "document" &&
        row.statusKey === "review" &&
        row.reviewerEmail?.trim().toLowerCase() === email,
    );
  }

  if (search && needSessions) {
    filtered = filtered.filter((row) => matchesSearch(row, search));
  }

  return [...filtered].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
};
